import { Box, Button, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { elevation, radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";

export interface StationCardProps {
  category: string;
  eventName: string;
  done: number;
  total: number;
  /** Visible status word, already translated (design: "Group 3 scoring", "Locked"). */
  statusLabel?: string;
  /** Lock label (already translated) shown when the station is locked. */
  lockLabel?: string;
  /** Event pictogram (an `EventIcon`) shown in the blue-soft tile at the top left. */
  icon?: ReactNode;
  /** Lock / Unlock pill at the top right (design `t.onLock`). */
  lockAction?: { label: string; ariaLabel: string; onClick: () => void };
}

/**
 * Session station card (design `s.session` stations): icon tile, category eyebrow, event name,
 * big done/total numerals and an 8 px progress bar.
 */
export const StationCard = ({
  category,
  eventName,
  done,
  total,
  statusLabel,
  lockLabel,
  icon,
  lockAction,
}: StationCardProps) => {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const complete = total > 0 && done >= total;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: `${radius.card}px`,
        boxShadow: elevation.card[mode],
        p: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        {icon ? (
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              flex: "none",
              borderRadius: `${radius.control}px`,
              bgcolor: "primary.light",
              display: "grid",
              placeItems: "center",
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            component="span"
            sx={{ display: "block", color: "dangerInk", lineHeight: 1 }}
          >
            {category}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}>
            {eventName}
          </Typography>
        </Box>
        {lockAction ? (
          <Button
            variant="outlined"
            size="small"
            onClick={lockAction.onClick}
            aria-label={lockAction.ariaLabel}
            sx={{
              ml: "auto",
              fontSize: 11,
              fontWeight: 700,
              minHeight: 30,
              px: 1.25,
              color: "text.secondary",
            }}
          >
            {lockAction.label}
          </Button>
        ) : lockLabel ? (
          <Typography
            variant="caption"
            sx={{
              ml: "auto",
              px: 1,
              py: 0.25,
              borderRadius: `${radius.pill}px`,
              bgcolor: "warning.light",
              color: "warning.dark",
              fontWeight: 700,
            }}
          >
            {lockLabel}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Box
          component="span"
          sx={{
            fontSize: 54,
            fontWeight: 800,
            lineHeight: 0.9,
            fontStretch: fontStretch.condensed,
          }}
        >
          {done}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: 22,
            fontWeight: 700,
            fontStretch: fontStretch.condensed,
            color: "text.disabled",
          }}
        >
          {`/${total}`}
        </Box>
        {statusLabel ? (
          <Typography
            component="span"
            sx={{
              ml: "auto",
              fontSize: 12,
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            {statusLabel}
          </Typography>
        ) : null}
      </Box>
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={t("sessions.stationProgress", {
          defaultValue: "{{event}} progress",
          event: eventName,
        })}
        sx={{
          height: 8,
          borderRadius: `${radius.pill}px`,
          bgcolor: "track",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: `${radius.pill}px`,
            bgcolor: complete ? "success.main" : "primary.main",
            boxShadow: elevation.glowPresidential[mode],
            transition: "width .6s",
          }}
        />
      </Box>
    </Box>
  );
};

export default StationCard;
